<template>
  <mi-dialog
    class="pac-edit-dialog"
    :model-value="modelValue"
    persistent
    @update:model-value="emitUpdateModelValue"
  >
    <h5 class="q-mt-none q-mb-lg">Edit Configuration</h5>

    <edit-form
      :initial-configuration="currentConfiguration"
      :is-edit="true"
      :error-message-unauthorized="errorMessageUnauthorized"
      @submit="editConfiguration"
    >
      <template #actions>
        <div class="flex justify-end">
          <mi-btn
            v-close-popup
            class="q-mr-sm"
            flat
            @click="onCancel"
          >
            Cancel
          </mi-btn>

          <mi-progress-btn
            :visible="showLoadingWarning"
            :duration-ms="DURATION_MS"
            :disabled="!isAuthorizedToEditCurrentConfiguration"
            @confirm="editConfiguration({ skipLoadingWarning: true })"
            @cancel="cancelFlow"
          >
            <template #button="{ seconds }">
              Confirm ({{ seconds }} sec)
            </template>

            <template #tooltip>
              Processing may take longer<br />
              while the system replaces product models
            </template>
          </mi-progress-btn>

          <mi-btn
            v-if="!showLoadingWarning"
            :loading="isConfigurationEditInProgress"
            :disable="!isAuthorizedToEditCurrentConfiguration"
            type="submit"
          >
            Edit
          </mi-btn>
        </div>
      </template>
    </edit-form>
  </mi-dialog>

  <product-model-change-dialog
    :is-same-product-model="false"
    :model-value="isProductModelChangeDialogOpen"
    :modified-elements="modifiedElements"
    :close-dialog="onHandleProductModelChangeDialog"
    :is-pac="true"
    @update:product-model="confirmEditingConfiguration"
  ></product-model-change-dialog>
</template>

<script>
  import { createNamespacedHelpers } from 'vuex'
  import { ref } from 'vue'

  import EditForm from '@/components/pac/new-configuration-stepper/NewConfigurationStepCreateForm.vue'
  import ProductModelChangeDialog from '@/components/product-model/ProductModelChangeDialog.vue'
  import MiProgressBtn from '@/packages/@mi-components/MiProgressBtn/MiProgressBtn.vue'

  import recordAnalytics from '@/utils/analytics/recordAnalytics'
  import { PAC_EDITED_CONFIG } from '@/utils/analytics/constants'
  import notify from '@/utils/notify'

  import cloneDeep from 'lodash.clonedeep'
  import { CLEAR_SELECTED_CHOICES } from '@/store/modules/pac/mutationTypes'
  import { INVALID_USERS_ERROR_MESSAGE, PM_CHANGE_TABS, PM_TYPE } from '@/constants'
  import { detectElementsChangeFromProductModel, showHTTPErrorNotification } from '../../../../api'

  const { mapActions, mapState, mapGetters, mapMutations } = createNamespacedHelpers('pac')

  export default {
    name: 'ConfigurationEditDialog',
    components: {
      MiProgressBtn,
      ProductModelChangeDialog,
      EditForm
    },
    props: {
      modelValue: {
        type: Boolean,
        required: true
      },
      options: {
        type: Object,
        required: true
      }
    },
    emits: ['update:model-value'],
    setup() {
      const DURATION_MS = 10000
      const showLoadingWarning = ref(false)
      const isCancelled = ref(false)

      const delay = ms => new Promise(resolve => setTimeout(resolve, ms))

      const cancelFlow = () => {
        isCancelled.value = true
        showLoadingWarning.value = false
      }

      return {
        DURATION_MS,
        showLoadingWarning,
        isCancelled,
        delay,
        cancelFlow
      }
    },

    data: () => ({
      isConfigurationEditInProgress: false,
      errorMessageUnauthorized: '',
      isProductModelChangeDialogOpen: false,
      modifiedElements: {
        options: [],
        components: [],
        completeOptions: [],
        completeComponents: [],
        pmIdentifier: ''
      },
      configurationToEdit: {}
    }),

    computed: {
      ...mapState(['currentConfiguration']),
      ...mapGetters(['isAuthorizedToEditCurrentConfiguration'])
    },

    methods: {
      ...mapActions(['updateConfiguration', 'getConfigurationOptions', 'loadConfiguration']),
      ...mapMutations({ CLEAR_SELECTED_CHOICES }),
      onCancel() {
        this.emitUpdateModelValue(false)
      },
      onHandleProductModelChangeDialog() {
        this.isProductModelChangeDialogOpen = !this.isProductModelChangeDialogOpen
      },

      isDifferentProductModel(newProductModel = {}) {
        if (!newProductModel?.encodedBusinessName) return false
        return this.currentConfiguration.productModel.encodedBusinessName !== newProductModel.encodedBusinessName
      },

      mapElementsOnlyWithSelectedChoices(elements, selectedChoices) {
        const selectedChoiceIdsByOptionId = selectedChoices.reduce((acc, sc) => {
          const optionId = sc?.choice?.optionId
          const choiceId = sc?.choice?.id
          if (!optionId || !choiceId) return acc

          if (!acc[optionId]) acc[optionId] = new Set()
          acc[optionId].add(choiceId)

          return acc
        }, {})

        return elements
          .filter(option => selectedChoiceIdsByOptionId[option.id])
          .map(option => {
            const allowedChoiceIds = selectedChoiceIdsByOptionId[option.id]
            return {
              ...option,
              choices: (option.choices || []).filter(choice => allowedChoiceIds.has(choice.id))
            }
          })
      },

      async checkProductModelChanges(newPm) {
        const params = {
          options: this.mapElementsOnlyWithSelectedChoices(
            this.options.items,
            this.currentConfiguration.selectedChoices
          ),
          components: [],
          pmInfo: {
            pmType: newPm.productModelType,
            pmIdentifier: newPm.productModelType === PM_TYPE.PRODUCT_MODEL
              ? newPm.encodedBusinessName
              : newPm.productModel.id
          },
          oldPmInfo: {
            pmType: PM_TYPE.PRODUCT_MODEL,
            pmIdentifier: this.currentConfiguration.productModel.encodedBusinessName
          }
        }

        try {
          return await detectElementsChangeFromProductModel(params)
        }
        catch (e) {
          notify({
            title: `HTTP Error ${ e.status || '' }`,
            content: 'Error when changing the Product Model',
            type: 'negative'
          })
          throw e
        }
      },

      async confirmEditingConfiguration(differentElements = {}) {
        if (differentElements?.detectElementsData?.options?.length) {
          this.isConfigurationEditInProgress = true
          const previousSelectedChoices = cloneDeep(this.currentConfiguration.selectedChoices)
          this.configurationToEdit.selectedChoices = previousSelectedChoices.filter(
            selectedChoice => differentElements.detectElementsData.options.some(
              option => option.choices.some(choice => choice.id === selectedChoice.choice.id)
            )
          )
        }

        await this.updateConfiguration(this.configurationToEdit)
        recordAnalytics(PAC_EDITED_CONFIG)

        if (differentElements?.detectElementsData?.options?.length) {
          this.CLEAR_SELECTED_CHOICES()
          await this.loadConfiguration(this.configurationToEdit.id, true)
          await this.getConfigurationOptions({ sort: 'name,asc' })
        }

        this.emitUpdateModelValue()
      },

      async editConfiguration({ configuration = {}, skipLoadingWarning = false } = {}) {
        this.configurationToEdit = !skipLoadingWarning ? configuration : this.configurationToEdit

        try {
          if (this.isDifferentProductModel(this.configurationToEdit.productModel)) {
            if (!skipLoadingWarning) {
              this.isCancelled = false
              this.showLoadingWarning = true

              await this.delay(this.DURATION_MS)

              this.showLoadingWarning = false
              if (this.isCancelled) return
            }

            this.isConfigurationEditInProgress = true

            const data = await this.checkProductModelChanges(this.configurationToEdit.productModel)

            const removedChoices = data?.modifiedOptions?.filter(
              option => option.modificationType === PM_CHANGE_TABS.ELEMENTS_TO_BE_REMOVED.modificationType
            ) || []

            if (removedChoices.length) {
              this.onHandleProductModelChangeDialog()
              this.modifiedElements = {
                options: removedChoices,
                components: [],
                completeOptions: data.options,
                completeComponents: data.components,
                pmIdentifier: this.configurationToEdit.productModel?.businessName
              }
            }
            else {
              await this.confirmEditingConfiguration()
              await this.getConfigurationOptions({ sort: 'name,asc' })
            }
          }
          else {
            this.isConfigurationEditInProgress = true
            await this.confirmEditingConfiguration()
          }
        }
        catch (error) {
          if (error.response?.data?.includes?.(INVALID_USERS_ERROR_MESSAGE)) {
            this.errorMessageUnauthorized = error.response?.data
          }
          else {
            showHTTPErrorNotification({ status: error.status, message: error.message })
          }
        }
        finally {
          this.isConfigurationEditInProgress = false
        }
      },

      emitUpdateModelValue(value = false) {
        if (!value) this.cancelFlow()
        this.isConfigurationEditInProgress = false
        this.$emit('update:model-value', value)
      }
    }
  }
</script>

<style lang="scss">
  .pac-edit-dialog {
    .q-dialog__inner > .mi-card {
      width: 924px;
      max-width: 924px;
      min-width: 580px;
    }
  }

  @media (max-width: $mi-responsive-large-width) {
    .pac-edit-dialog {
      .q-dialog__inner > .mi-card {
        width: 683px;
        max-width: 683px;
      }
    }
  }

  @media (max-width: $mi-responsive-medium-width) {
    .pac-edit-dialog {
      .q-dialog__inner > .mi-card {
        width: 477px;
        max-width: 477px;
      }
    }
  }
</style>
